import type { MarketProgramType } from '@/types/marketProgramType'

export const MARKET_PROGRAMS: MarketProgramType[] = [
  'afrr-down',
  'afrr-up',
  'fcrd-down',
  'fcrd-up',
  'fcrn',
  'ffr-up',
  'load-cur',
  'max-price',
  'mfrr-down',
  'mfrr-up',
  'mfrrda-down',
  'mfrrda-up',
  'mfrr-rr-down',
  'mfrr-rr-up',
  'nordics-fcrd-down-dynamic',
  'nordics-fcrd-down-static',
  'nordics-fcrd-up-dynamic',
  'nordics-fcrd-up-static',
  'nordics-fcrn',
  'fcr-d-down-dynamic',
  'fcr-d-up-dynamic',
  'fcr-d-up-static',
  'fcr-n',
  'symbalance-down',
  'symbalance-up',
]

export const MARKET_PROGRAMS_COLOR_PALETTE = [
  '#6D8AB4',
  '#E5ADAA',
  '#753210',
  '#C4A8D8',
  '#e8886b',
  '#7BC4C4',
  '#D6C36E',
  '#79C1A3',
  '#9CB8D6',
  '#B8CC7A',
  '#E2B8C4',
  '#6EAAB4',
  '#D1A87B',
  '#A7D2E2',
  '#B09ED6',
  '#3f19b8',
  '#F0B87E',
  '#D89E9E',
  '#82B5D1',
  '#C7A1B8',
  '#84A97F',
  '#E9D4A4',
  '#D4A5A5',
  '#d67c96',
  '#A8C85E',
  '#ff0000',
  '#c88f00',
  '#A3C9A8',
]

/**
 * Fixed color mapping for all market program types including granular revenue variants.
 * This map intentionally includes types beyond MarketProgramType (e.g. capacity/energy variants)
 * to ensure consistent chart colors on the Revenue page.
 *
 * Colors are assigned per program explicitly (not by list position) so that adding a program
 * can never shift the colors of existing ones or make two programs share a color. The
 * `satisfies` clause forces every MarketProgramType to have an entry.
 */
const CAPACITY_ENERGY_MARKET_PROGRAM_COLOR_MAP: Record<string, string> = {
  'afrr-down': '#6D8AB4',
  'afrr-up': '#E5ADAA',
  'fcrd-down': '#753210',
  'fcrd-up': '#C4A8D8',
  fcrn: '#e8886b',
  'ffr-up': '#7BC4C4',
  'load-cur': '#D6C36E',
  'max-price': '#79C1A3',
  'mfrr-down': '#9CB8D6',
  'mfrr-up': '#B8CC7A',
  'mfrrda-down': '#E2B8C4',
  'mfrrda-up': '#6EAAB4',
  'mfrr-rr-down': '#D1A87B',
  'mfrr-rr-up': '#A7D2E2',
  'nordics-fcrd-down-dynamic': '#B09ED6',
  'nordics-fcrd-down-static': '#3f19b8',
  'nordics-fcrd-up-dynamic': '#F0B87E',
  'nordics-fcrd-up-static': '#D89E9E',
  'nordics-fcrn': '#82B5D1',
  'fcr-d-down-dynamic': '#C7A1B8',
  'fcr-d-up-dynamic': '#84A97F',
  'fcr-d-up-static': '#E9D4A4',
  'fcr-n': '#D4A5A5',
  'symbalance-down': '#664488',
  'symbalance-up': '#668800',
  // Granular capacity/energy revenue variants, not part of MarketProgramType
  'mfrr-down-energy': '#d67c96',
  'mfrr-down-capacity': '#A8C85E',
  'mfrr-up-capacity': '#ff0000',
  'mfrr-up-energy': '#c88f00',
  'fcr-n-capacity': '#A3C9A8',
  'fcr-n-energy': '#cc3377',
} satisfies Record<MarketProgramType, string> & Record<string, string>

/**
 * BRP billing-line market programs returned by the insights revenues API.
 * Unlike capacity/energy market programs, they carry no sold capacity or activated energy.
 */
export const BRP_MARKET_PROGRAM_TYPES = [
  'balancing-fees',
  'day-ahead',
  'imbalance',
  'intraday-auction',
  'intraday-continuous',
  'wholesale-fees',
] as const

/**
 * The subset of BRP market programs that are fees, i.e. considered costs. All other market
 * programs (including the remaining BRP ones, such as day-ahead) are considered revenues.
 */
export const FEE_MARKET_PROGRAM_TYPES = ['balancing-fees', 'imbalance', 'wholesale-fees'] as const

// BRP programs get explicit colors outside the shared palette so they stay visually
// distinguishable from capacity/energy market programs on the Revenue charts.
const BRP_MARKET_PROGRAM_COLOR_MAP: Record<string, string> = {
  'balancing-fees': '#8C564B',
  'day-ahead': '#1F77B4',
  imbalance: '#E377C2',
  'intraday-auction': '#9467BD',
  'intraday-continuous': '#17BECF',
  'wholesale-fees': '#5D6D7E',
}

export const MARKET_PROGRAM_COLOR_MAP: Record<string, string> = {
  ...CAPACITY_ENERGY_MARKET_PROGRAM_COLOR_MAP,
  ...BRP_MARKET_PROGRAM_COLOR_MAP,
}
